'use client';
import { objectKeys } from '@s7/utils';
import { deriveBaseShades } from './derivations/deriveBaseShades/deriveBaseShades.js';
import { deriveTextShades } from './derivations/deriveTextShades/deriveTextShades.js';
import { deriveBackgroundShades } from './derivations/deriveBackgroundShades/deriveBackgroundShades.js';
import { rgba } from '../../../../fns/rgba/rgba.js';

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
const getColorShades = (color, overrides = {})=>{
    const [colorName] = objectKeys(color);
    if (!color[colorName]) {
        return {};
    }
    const shades = color[colorName];
    const overrideKeys = objectKeys(overrides);
    let override = {};
    for (const shadeKey of overrideKeys){
        const [dayShade, nightShade] = overrides[shadeKey];
        override = _extends({}, override, {
            [shadeKey]: {
                day: shades[dayShade],
                night: shades[nightShade]
            }
        });
    }
    return {
        [colorName]: _extends({}, deriveBaseShades(color[colorName]), override)
    };
};
const createPalette = (colors, invariants)=>{
    const { green, blue, orange, red, turquoise, violet, emerald, yellow, pink, brand } = colors;
    const { neutral, white, black } = invariants;
    const semanticGreen = green ? deriveBaseShades(green) : undefined;
    /*
     * Вычисление дополнительных семантических цветов здесь
     * позволяет избежать появления логики с множеством исключений,
     * размазанной по всем компонентам.
     * */ return _extends({}, semanticGreen && {
        primary: semanticGreen,
        success: semanticGreen,
        miles: semanticGreen
    }, blue && {
        info: _extends({}, deriveBaseShades(blue), {
            base: {
                day: blue[600],
                night: blue[600]
            }
        })
    }, orange && {
        attention: deriveBaseShades(orange)
    }, red && {
        disruption: deriveBaseShades(red)
    }, {
        text: _extends({}, deriveTextShades(neutral, white), brand && {
            brand: {
                day: brand[600],
                night: brand[600]
            }
        }),
        background: deriveBackgroundShades(neutral, white)
    }, neutral && {
        border: _extends({
            card: {
                day: rgba(neutral[80], 0.2),
                night: rgba(neutral[100], 0.2)
            },
            divider: {
                day: rgba(neutral[80], 0.3),
                night: rgba(neutral[100], 0.3)
            },
            element: {
                day: rgba(neutral[80], 0.4),
                night: rgba(neutral[100], 0.4)
            },
            activeElement: {
                day: rgba(neutral[80], 0.9),
                night: rgba(neutral[100], 0.9)
            }
        }, brand && {
            brandSecondary: {
                day: brand[50],
                night: brand[900]
            },
            brand: {
                day: brand[600],
                night: brand[600]
            }
        }),
        tooltip: {
            background: {
                day: neutral[500],
                night: neutral[40]
            }
        },
        cookie: {
            background: {
                day: rgba(neutral[900], 0.92),
                night: rgba(neutral[30], 0.92)
            }
        },
        snackbar: {
            background: {
                day: neutral[900],
                night: neutral[30]
            }
        },
        modal: {
            background: {
                day: neutral[30],
                night: neutral[920]
            }
        }
    }, {
        button: _extends({}, neutral && {
            secondary: {
                day: neutral[40],
                night: neutral[700]
            },
            translucent: {
                day: rgba(neutral[40], 0.08),
                night: rgba(neutral[700], 0.08)
            }
        }, {
            white: _extends({}, white && {
                day: white[100]
            }, blue && {
                night: rgba(blue[50], 0.09)
            })
        }),
        tabsnav: _extends({}, neutral && {
            background: {
                day: rgba(neutral[20], 0.85),
                night: rgba(neutral[920], 0.85)
            }
        }, {
            tabActive: {
                day: '#8CC52F',
                night: '#7CB124'
            }
        }),
        mixture: _extends({}, blue && {
            light: {
                day: blue[950],
                night: blue[200]
            }
        }, neutral && black && {
            color: {
                day: neutral[900],
                night: black[100]
            }
        }, neutral && {
            dark: {
                day: neutral[920],
                night: neutral[20]
            }
        }, neutral && white && {
            stronger: {
                day: white[100],
                night: neutral[920]
            }
        }),
        other: _extends({}, neutral && black && {
            shadow: {
                day: neutral[900],
                night: black[100]
            }
        }, {
            fade: {
                day: '#0000004d',
                night: '#00000099'
            }
        }, white && {
            fadeBrightness: {
                day: white[0],
                night: '#222a3726'
            }
        }),
        promo: {
            deepGreen: '#098D41',
            green: '#50982F',
            seagreen: '#3EC075',
            superSeagreen: '#11EC7B',
            lime: '#C4D600',
            superLime: '#C2F006',
            superPistachio: '#B7ECB8',
            superBlue: '#3D53EE',
            tabrizBlue: '#007F9D',
            aquamarine: '#36C0C0',
            aqua: '#8EDEE6',
            superAqua: '#16ECE1',
            darkViolet: '#801F7E',
            purple: '#512D6D',
            mediumPurple: '#614D94',
            darkSlate: '#444791',
            superPurple: '#8A8AEE',
            scarlet: '#A83160',
            brightRed: '#E4445C',
            salamon: '#FF7B7B',
            candyPink: '#FFA8BB',
            citrine: '#FF961A',
            sand: '#FFA766',
            sorbetYellow: '#F3E169',
            superYellow: '#F3FF0B'
        }
    }, getColorShades({
        blue
    }, {
        base: [
            600,
            600
        ]
    }), getColorShades({
        green
    }), getColorShades({
        orange
    }), getColorShades({
        red
    }), getColorShades({
        turquoise
    }, {
        base: [
            600,
            600
        ]
    }), getColorShades({
        violet
    }, {
        base: [
            600,
            600
        ]
    }), getColorShades({
        pink
    }), getColorShades({
        emerald
    }), getColorShades({
        yellow
    }));
};

export { createPalette };
//# sourceMappingURL=createPalette.js.map
